import * as React from 'react';
import { ReactStripeElements, injectStripe } from 'react-stripe-elements';
import AdminUpgradePlanFlowEnd from 'admin/components/AdminUpgradePlan/AdminUpgradePlanFlowEnd';
import AdminUpgradePlanForms from 'admin/components/AdminUpgradePlan/AdminUpgradePlanForms';
import AdminUpgradePlanInfoInputForm from 'admin/components/AdminUpgradePlan/AdminUpgradePlanInfoInputForm';
import AdminUpgradePlanPaymentForm from 'admin/components/AdminUpgradePlan/AdminUpgradePlanPaymentForm';
import AdminUpgradePlanQuoteForm from 'admin/components/AdminUpgradePlan/AdminUpgradePlanQuoteForm';

// @ts-ignore
import AuthActions from 'auth/AuthActions';
// @ts-ignore
import { createSelfServeHelpRequest } from 'admin/actions/AdminUpgradePlanActions';
import { largeOrgThreshold } from 'constants/SelfServeConstants';
import { OrganizationType, SelfServeHelpScreenSource } from 'globalTypes';
import { priceInCents, getBlankQuote } from 'utils/selfServeHelpers';
import ToastActions from 'actions/ToastActions';
import {
  convertSelfServeQuote,
  updateQuote,
  ActionResponse,
  QuotePayload,
  filterQuoteUpdates,
} from 'admin/actions/AdminUpgradePlanActions';
// @ts-ignore
import { promiseTelemetry } from 'lib/telemetry';
// @ts-ignore
import { configureRollbar } from 'utils/setupErrorLogging';
import { shouldStripeErrorBeReported, reportStripeErrorToRollbar } from 'utils/selfServeHelpers';

import GTMScript from 'components/GTMScript';

import './styles/AdminUpgradePlanContainer.scss';

export const isLargeOrganization = (studentsCount: number) => {
  return studentsCount > largeOrgThreshold;
};

export type UpgradePlanTabKey =
  | 'org-info'
  | 'quote'
  | 'payment'
  | 'purchase-complete'
  | 'contact-sales';

type PaymentMethod = 'card';

export type UpgradePlanData = {
  country?: string;
  state?: string;
  city?: string;
  orgUuid?: string;
  initialOrgUuid?: string;
  displayOrgStats?: boolean;
  lat?: number;
  long?: number;
  teachersCount?: number;
  studentsCount?: number;
  comment?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
  requesterRole?: string;
  requesterPhone?: string;
  requesterEmail?: string;
  orgName?: string;
  orgAddress?: string;
  orgType?: OrganizationType;
  startDate?: string;
  endDate?: string;
  price?: string;
  taxExempt?: boolean;
  tax?: string;
  billingFirstName?: string;
  billingLastName?: string;
  billingEmail?: string;
  useRequesterAsBillingContact?: boolean;
  adminVerificationUrl?: string;
  purchaseSuccessful: boolean;
};

export type UpgradePlanQuote = {
  uuid: string;
  studentsCount: number;
  teachersCount: number;
  startDate: string;
  endDate: string;
  taxExempt: boolean;
  price: string;
  tax: string;
  total: string;
  expirationDate: string;
  shareToken: string;
  organization: Organization;
  organizationAddress: OrganizationAddress;
};

export type UpgradePlanTab = {
  tabKey: UpgradePlanTabKey;
  component: React.ComponentClass<any>;
  nextStep?: () => UpgradePlanTabKey;
  prevStep?: () => UpgradePlanTabKey;
  isComplete: boolean;
  isSimpleForm?: boolean;
};

type Organization = {
  id: number;
  uuid: string;
  name: string;
  city: string;
  state: string;
};

type OrganizationAddress = {
  latitude?: number;
  longitude?: number;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
};

type Props = {
  orgUuid?: string;
  isSimpleForm: boolean;
  quote?: UpgradePlanQuote;
  jumpToTab?: UpgradePlanTabKey;
};

type State = {
  upgradePlanData: UpgradePlanData;
  upgradePlanQuote: UpgradePlanQuote;
  currentTabKey: UpgradePlanTabKey;
  paymentMethod: PaymentMethod;
  cardError?: string;
  cardInputCompleted: boolean;
  submittingPayment: boolean;
};

class AdminUpgradePlanContainer extends React.Component<
  Props & ReactStripeElements.InjectedStripeProps,
  State
> {
  constructor(props: Props) {
    super(props);
  }

  state: State = {
    upgradePlanData: {
      country: '',
      state: '',
      city: '',
      orgUuid: '',
      initialOrgUuid: this.props.orgUuid ?? '',
      displayOrgStats: false,
      lat: 0,
      long: 0,
      teachersCount: 0,
      studentsCount: 0,
      comment: '',
      requesterFirstName: '',
      requesterLastName: '',
      requesterRole: '',
      requesterPhone: '',
      requesterEmail: '',
      orgName: '',
      orgAddress: '',
      orgType: OrganizationType.School,
      startDate: '',
      endDate: '',
      price: '',
      taxExempt: false,
      tax: '$0.00',
      purchaseSuccessful: false,
    },
    upgradePlanQuote: this.props.quote ? this.props.quote : getBlankQuote(),
    currentTabKey: this.props.jumpToTab ? this.props.jumpToTab : ('org-info' as UpgradePlanTabKey),
    paymentMethod: 'card' as PaymentMethod,
    cardInputCompleted: false,
    submittingPayment: false,
  };

  async componentDidMount() {
    try {
      await AuthActions.checkLoggedIn();
      this.showOrgStats();
    } catch (e) {
      // we want rollbar in this context even if the user is unauthed
      await promiseTelemetry().then(() => configureRollbar({ id: -1 }));
      this.hideOrgStats();
    }
  }

  showOrgStats = () => {
    this.setState({ upgradePlanData: { ...this.state.upgradePlanData, displayOrgStats: true } });
  };

  hideOrgStats = () => {
    this.setState({ upgradePlanData: { ...this.state.upgradePlanData, displayOrgStats: false } });
  };

  saveAdminUpgradePlanData = (
    fields: UpgradePlanData,
    quote: UpgradePlanQuote,
    onSuccess: Function
  ) => {
    this.setState(
      {
        upgradePlanData: {
          ...this.state.upgradePlanData,
          ...fields,
        },
        upgradePlanQuote: {
          ...this.state.upgradePlanQuote,
          ...quote,
        },
      },
      () => {
        onSuccess();
      }
    );
  };

  submitPayment = async (onSuccess: () => void) => {
    this.setState({ submittingPayment: true });
    const { upgradePlanData, upgradePlanQuote } = this.state;
    const {
      billingFirstName,
      billingLastName,
      billingEmail,
      requesterPhone,
      taxExempt,
    } = upgradePlanData;

    const hasBillingUpdate = billingFirstName && billingLastName && billingEmail && requesterPhone;
    if (hasBillingUpdate || taxExempt) {
      updateQuote(
        upgradePlanQuote.uuid,
        filterQuoteUpdates(upgradePlanQuote, upgradePlanData)
      ).then(resp => this.handleQuoteUpdateResponse(onSuccess)(resp));
    } else {
      this.chargeCard(onSuccess);
    }
  };

  handleQuoteUpdateResponse = (onSuccess: () => void) => {
    return (resp: ActionResponse<QuotePayload>) => {
      if (resp.success) {
        if (resp.quote) {
          this.setState({ upgradePlanQuote: resp.quote }, () => this.chargeCard(onSuccess));
        } else {
          return this.chargeCard(onSuccess);
        }
        return;
      }
      ToastActions.showErrorToast(
        'There was a problem updating your billing details. Please try again.'
      );
      this.setState({ submittingPayment: false });
      return;
    };
  };

  chargeCard = async (onSuccess: () => void) => {
    const { upgradePlanData, upgradePlanQuote } = this.state;
    // TODO: need to update the quote first if different billing
    // details were set
    const { orgUuid } = upgradePlanData;
    const { uuid: quoteUuid, organization, total } = upgradePlanQuote;

    const { source, error } = await this.props.stripe!.createSource();
    if (!source) {
      ToastActions.showErrorToast(
        error?.message ?? 'There was a problem saving your card. Please try again.'
      );
      console.warn(error?.message);
      if (error && shouldStripeErrorBeReported(error)) {
        reportStripeErrorToRollbar('CoachingBillingStripe - CoachingStripe: There was an error creating the source.', error);
      }
      this.setState({ submittingPayment: false });
      return;
    }
    return convertSelfServeQuote(
      source.id,
      organization.uuid,
      organization.id,
      priceInCents(total),
      quoteUuid
    ).then(resp => {
      if (resp.success) {
        ToastActions.showSuccessToast('Your card has been charged. We’ll be in touch.');
        this.setState({ submittingPayment: false });
        return onSuccess();
      }
      ToastActions.showErrorToast('There was a problem processing your payment. Please try again.');

      let newState = { cardError: undefined, submittingPayment: false };
      if (resp.declineReason) {
        newState = { cardError: resp.declineReason, ...newState };
      }
      this.setState(newState);
    });
  };

  createHelpRequest = async () => {
    const { upgradePlanData, upgradePlanQuote } = this.state;
    return createSelfServeHelpRequest({
      requesterPhoneNumber: `+${upgradePlanData.requesterPhone}`!,
      studentsCount: Number(upgradePlanData.studentsCount),
      teachersCount: Number(upgradePlanData.teachersCount),
      organizationUuid: upgradePlanQuote.organization.uuid,
      requesterFirstName: upgradePlanData.requesterFirstName,
      requesterLastName: upgradePlanData.requesterLastName,
      requesterEmail: upgradePlanData.requesterEmail,
      requesterRole: upgradePlanData.requesterRole,
      source: SelfServeHelpScreenSource.self_serve_v2,
    }).then(result => {
      if (!result?.success) {
        ToastActions.showErrorToast(result.errorMessage);
        return false;
      }
      return true;
    });
  };

  onTaxExemptChange = (taxExempt: boolean) => {
    this.setState({ upgradePlanData: { ...this.state.upgradePlanData, taxExempt } });
  };

  render() {
    const {
      upgradePlanData,
      upgradePlanQuote,
      currentTabKey,
      submittingPayment,
      cardInputCompleted,
      cardError,
    } = this.state;
    const { isSimpleForm } = this.props;
    const tabs = [
      {
        tabKey: 'org-info',
        component: AdminUpgradePlanInfoInputForm,
        prevStep: undefined,
        nextStep: () => {
          const { upgradePlanData } = this.state;
          const { studentsCount } = upgradePlanData;
          if ((studentsCount && isLargeOrganization(studentsCount)) || isSimpleForm) {
            return 'contact-sales';
          }
          return 'quote';
        },
        isComplete: false,
      },
      {
        tabKey: 'quote',
        component: AdminUpgradePlanQuoteForm,
        prevStep: () => 'org-info',
        nextStep: () => 'quote-payment',
        isComplete: false,
      },
      {
        tabKey: 'quote-payment',
        component: AdminUpgradePlanPaymentForm,
        prevStep: () => 'quote',
        nextStep: () => 'purchase-complete',
        isComplete: false,
      },
      {
        tabKey: 'contact-sales',
        component: AdminUpgradePlanFlowEnd,
        prevStep: undefined,
        nextStep: undefined,
        isComplete: true,
      },
      {
        tabKey: 'purchase-complete',
        component: AdminUpgradePlanFlowEnd,
        prevStep: () => 'quote-payment',
        nextStep: undefined,
        isComplete: true,
      },
    ] as UpgradePlanTab[];
    const currentTab = tabs.find(tab => tab.tabKey === currentTabKey);
    return (
      <div className="AdminUpgradePlanContainer">
        <GTMScript />
        <AdminUpgradePlanForms
          tabs={tabs}
          currentTab={currentTab}
          fields={upgradePlanData}
          quote={upgradePlanQuote}
          cardError={cardError}
          onNoOrgStats={() => this.hideOrgStats()}
          onCardInputCompleted={() => this.setState({ cardInputCompleted: true })}
          onPersistState={this.saveAdminUpgradePlanData}
          onTaxExemptChange={this.onTaxExemptChange}
          onAcceptPlanTerms={this.submitPayment}
          onContactSales={this.createHelpRequest}
          submittingPayment={submittingPayment}
          cardInputCompleted={cardInputCompleted}
          isSimpleForm={isSimpleForm}
        />
      </div>
    );
  }
}

export { AdminUpgradePlanContainer };
export default injectStripe(AdminUpgradePlanContainer);
